import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  FolderKanban,
  Wrench,
  Package2,
  Truck,
  Boxes,
  FileText,
  DollarSign,
  Package,
  Users,
  Bot,
  ArrowRight,
  ArrowDown,
  CheckCircle2,
  HelpCircle,
  BookOpen,
} from "lucide-react";

const Help = () => {
  const [activeTab, setActiveTab] = useState("overview");

  const entities = [
    {
      name: "Projects",
      icon: FolderKanban,
      color: "bg-blue-100 text-blue-700",
      description: "Manufacturing jobs or orders from clients",
      contains: ["Project name, client, status, dates", "Linked operations (work to be done)", "Linked materials (items needed)", "Linked expenses (costs incurred)"],
      example: {
        title: "Custom Steel Gate for Client ABC",
        items: ["Operations: Cutting, Welding, Painting", "Materials: Steel sheets, Hinges, Paint", "Expenses: Labor, Transport, Consumables"]
      }
    },
    {
      name: "Operations",
      icon: Wrench,
      color: "bg-orange-100 text-orange-700",
      description: "Manufacturing tasks/work performed on a project",
      contains: ["Operation type (Cutting, Welding, etc.)", "Assigned team member", "Status tracking", "Notes and details"],
      example: {
        title: "Welding - Steel Gate",
        items: ["Project: Custom Steel Gate", "Assigned to: John (Welder)", "Status: In Progress"]
      }
    },
    {
      name: "Standard Items",
      icon: Package2,
      color: "bg-purple-100 text-purple-700",
      description: "Reusable templates for products you manufacture repeatedly",
      contains: ["Item name, SKU, description", "Bill of materials (required items)", "Default operations", "Estimated cost"],
      example: {
        title: "Standard Security Gate",
        items: ["Materials: Steel tubing, Hinges, Lock", "Operations: Cutting, Welding, Painting", "Est. Cost: R 5,500"]
      }
    },
    {
      name: "Suppliers",
      icon: Truck,
      color: "bg-green-100 text-green-700",
      description: "Companies you purchase materials from",
      contains: ["Name, contact person, email, phone", "Category (Steel, Hardware, etc.)", "Linked to inventory items"],
      example: {
        title: "Steel World SA",
        items: ["Category: Raw Materials", "Contact: orders@steelworld.co.za", "Supplies: Steel sheets, tubing, bolts"]
      }
    },
    {
      name: "Inventory",
      icon: Boxes,
      color: "bg-indigo-100 text-indigo-700",
      description: "Stock of materials/items in your warehouse",
      contains: ["Current stock level", "Min/Max thresholds", "Unit cost", "Preferred supplier"],
      example: {
        title: "Steel Bolts M10",
        items: ["Current: 50 pcs", "Min Threshold: 100 pcs ⚠️", "Unit Cost: R 12.50"]
      }
    },
    {
      name: "Purchase Orders",
      icon: FileText,
      color: "bg-cyan-100 text-cyan-700",
      description: "Formal orders sent to suppliers for inventory",
      contains: ["PO number, supplier", "Line items with quantities & prices", "VAT calculation (15%)", "PDF generation & email"],
      example: {
        title: "PO-2025-001",
        items: ["Supplier: Hardware Depot", "Items: Steel Bolts (200 pcs)", "Total: R 3,450 incl. VAT"]
      }
    },
    {
      name: "Materials",
      icon: Package,
      color: "bg-amber-100 text-amber-700",
      description: "Materials ordered/needed for a specific project",
      contains: ["Material name, quantity, unit", "Order status", "Supplier & delivery date"],
      example: {
        title: "Materials for Steel Gate",
        items: ["Steel Sheets (5) - Delivered ✓", "Hinges (2) - Ordered", "Paint (2L) - Pending"]
      }
    },
    {
      name: "Expenses",
      icon: DollarSign,
      color: "bg-rose-100 text-rose-700",
      description: "Costs incurred on projects or business",
      contains: ["Amount, category, date", "Project link (optional)", "Description"],
      example: {
        title: "Welding Consumables",
        items: ["Project: Steel Gate", "Category: Consumables", "Amount: R 350"]
      }
    },
    {
      name: "AI Stock Controller",
      icon: Bot,
      color: "bg-violet-100 text-violet-700",
      description: "AI-powered assistant for inventory management",
      contains: ["Monitors stock vs thresholds", "Generates reorder suggestions", "Answers inventory questions", "Recommends suppliers"],
      example: {
        title: "AI Suggestion",
        items: ["Steel Bolts low (50/100)", "Suggested: Order 200 pcs", "From: Hardware Depot"]
      }
    },
  ];

  const workflowSteps = [
    { step: 1, title: "Client Request", desc: "Client wants a custom steel gate", icon: "📋" },
    { step: 2, title: "Create Project", desc: "Project: 'Steel Gate for Client XYZ'", icon: "📁" },
    { step: 3, title: "Add Standard Item", desc: "Use template to auto-add materials & operations", icon: "📦" },
    { step: 4, title: "Assign Operations", desc: "Cutting → Mike, Welding → John, Painting → Sarah", icon: "👷" },
    { step: 5, title: "Check Inventory", desc: "AI detects: Steel Bolts low (50/100)", icon: "🤖" },
    { step: 6, title: "Create Purchase Order", desc: "Order 200 Steel Bolts, submit for approval", icon: "📝" },
    { step: 7, title: "Approve & Send", desc: "Manager approves, email sent to supplier with PDF", icon: "✉️" },
    { step: 8, title: "Receive Materials", desc: "Mark PO received, inventory auto-updates to 250", icon: "📥" },
    { step: 9, title: "Track Expenses", desc: "Materials R2,500 + Labor R1,500 + Transport R300", icon: "💰" },
    { step: 10, title: "Complete Operations", desc: "All operations marked complete", icon: "✅" },
  ];

  const relationships = [
    { from: "Project", to: "Operations", desc: "A project has many operations" },
    { from: "Project", to: "Materials", desc: "A project needs materials" },
    { from: "Project", to: "Expenses", desc: "A project incurs expenses" },
    { from: "Standard Item", to: "Project", desc: "Templates can be added to projects" },
    { from: "Supplier", to: "Inventory", desc: "Supplier provides inventory items" },
    { from: "Supplier", to: "Purchase Orders", desc: "Supplier receives POs" },
    { from: "Inventory", to: "Purchase Orders", desc: "PO contains inventory items" },
    { from: "Purchase Order", to: "Inventory", desc: "Receiving PO updates stock" },
    { from: "AI Controller", to: "Inventory", desc: "AI monitors stock levels" },
    { from: "Team", to: "Operations", desc: "Team members perform operations" },
  ];

  return (
    <div className="p-6 md:p-8" data-testid="help-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
            <BookOpen className="h-5 w-5 text-slate-600" />
          </div>
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
              Help & Documentation
            </h1>
            <p className="text-slate-500">
              Understanding TaskForge workflows and entity relationships
            </p>
          </div>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-sm">
          <TabsTrigger value="overview" className="rounded-sm data-[state=active]:bg-white">
            Overview
          </TabsTrigger>
          <TabsTrigger value="entities" className="rounded-sm data-[state=active]:bg-white">
            Entities
          </TabsTrigger>
          <TabsTrigger value="workflow" className="rounded-sm data-[state=active]:bg-white">
            Workflow
          </TabsTrigger>
          <TabsTrigger value="relationships" className="rounded-sm data-[state=active]:bg-white">
            Relationships
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card className="rounded-sm border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">System Overview</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Visual Flow Diagram */}
              <div className="py-8 px-4 overflow-x-auto">
                <div className="min-w-[700px]">
                  {/* Row 1: Projects */}
                  <div className="flex justify-center mb-2">
                    <div className="bg-blue-500 text-white px-6 py-3 rounded-lg shadow-md font-semibold flex items-center gap-2">
                      <FolderKanban className="h-5 w-5" />
                      PROJECTS
                    </div>
                  </div>
                  
                  {/* Arrow down with branches */}
                  <div className="flex justify-center mb-2">
                    <div className="flex flex-col items-center">
                      <div className="w-0.5 h-6 bg-slate-300"></div>
                      <div className="flex items-center">
                        <div className="w-32 h-0.5 bg-slate-300"></div>
                        <div className="w-0.5 h-6 bg-slate-300"></div>
                        <div className="w-32 h-0.5 bg-slate-300"></div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Row 2: Operations, Expenses, Materials */}
                  <div className="flex justify-center gap-8 mb-2">
                    <div className="flex flex-col items-center">
                      <ArrowDown className="h-4 w-4 text-slate-400 mb-1" />
                      <div className="bg-orange-500 text-white px-4 py-2 rounded-lg shadow-md text-sm font-medium flex items-center gap-2">
                        <Wrench className="h-4 w-4" />
                        Operations
                      </div>
                      <span className="text-xs text-slate-500 mt-1">Work Done</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <ArrowDown className="h-4 w-4 text-slate-400 mb-1" />
                      <div className="bg-rose-500 text-white px-4 py-2 rounded-lg shadow-md text-sm font-medium flex items-center gap-2">
                        <DollarSign className="h-4 w-4" />
                        Expenses
                      </div>
                      <span className="text-xs text-slate-500 mt-1">Costs</span>
                    </div>
                    <div className="flex flex-col items-center">
                      <ArrowDown className="h-4 w-4 text-slate-400 mb-1" />
                      <div className="bg-amber-500 text-white px-4 py-2 rounded-lg shadow-md text-sm font-medium flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Materials
                      </div>
                      <span className="text-xs text-slate-500 mt-1">Orders</span>
                    </div>
                  </div>
                  
                  {/* Connector line */}
                  <div className="flex justify-center mb-2">
                    <div className="w-0.5 h-8 bg-slate-300 ml-[272px]"></div>
                  </div>
                  
                  {/* Row 3: Standard Items, Inventory, Suppliers */}
                  <div className="flex justify-center items-center gap-4 mb-2">
                    <div className="flex flex-col items-center">
                      <div className="bg-purple-500 text-white px-4 py-2 rounded-lg shadow-md text-sm font-medium flex items-center gap-2">
                        <Package2 className="h-4 w-4" />
                        Standard Items
                      </div>
                      <span className="text-xs text-slate-500 mt-1">Templates</span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                    <div className="flex flex-col items-center">
                      <div className="bg-indigo-500 text-white px-4 py-2 rounded-lg shadow-md text-sm font-medium flex items-center gap-2">
                        <Boxes className="h-4 w-4" />
                        Inventory
                      </div>
                      <span className="text-xs text-slate-500 mt-1">Stock</span>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400 rotate-180" />
                    <div className="flex flex-col items-center">
                      <div className="bg-green-500 text-white px-4 py-2 rounded-lg shadow-md text-sm font-medium flex items-center gap-2">
                        <Truck className="h-4 w-4" />
                        Suppliers
                      </div>
                      <span className="text-xs text-slate-500 mt-1">Vendors</span>
                    </div>
                  </div>
                  
                  {/* Connector lines to Purchase Orders */}
                  <div className="flex justify-center mb-2">
                    <div className="flex items-end gap-[180px]">
                      <div className="w-0.5 h-8 bg-slate-300"></div>
                      <div className="w-0.5 h-8 bg-slate-300"></div>
                    </div>
                  </div>
                  
                  {/* Row 4: Purchase Orders */}
                  <div className="flex justify-center mb-2">
                    <div className="flex flex-col items-center">
                      <div className="flex items-center gap-2 mb-1">
                        <ArrowDown className="h-4 w-4 text-slate-400" />
                        <ArrowDown className="h-4 w-4 text-slate-400" />
                      </div>
                      <div className="bg-cyan-500 text-white px-5 py-2.5 rounded-lg shadow-md font-medium flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Purchase Orders
                      </div>
                      <span className="text-xs text-slate-500 mt-1">Supplier Orders</span>
                    </div>
                  </div>
                  
                  {/* Connector to AI */}
                  <div className="flex justify-center mb-2">
                    <div className="w-0.5 h-8 bg-slate-300"></div>
                  </div>
                  
                  {/* Row 5: AI Stock Controller */}
                  <div className="flex justify-center">
                    <div className="flex flex-col items-center">
                      <ArrowDown className="h-4 w-4 text-slate-400 mb-1" />
                      <div className="bg-gradient-to-r from-violet-500 to-purple-600 text-white px-5 py-2.5 rounded-lg shadow-md font-medium flex items-center gap-2">
                        <Bot className="h-4 w-4" />
                        AI Stock Controller
                      </div>
                      <span className="text-xs text-slate-500 mt-1">Smart Suggestions</span>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Legend */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <p className="text-sm font-medium text-slate-700 mb-3">Legend</p>
                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-blue-500"></div>
                    <span className="text-xs text-slate-600">Core Entity</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-indigo-500"></div>
                    <span className="text-xs text-slate-600">Inventory System</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded bg-gradient-to-r from-violet-500 to-purple-600"></div>
                    <span className="text-xs text-slate-600">AI Powered</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-3 w-3 text-slate-400" />
                    <span className="text-xs text-slate-600">Data Flow</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="rounded-sm border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                    <FolderKanban className="h-5 w-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold">Project Management</h3>
                </div>
                <p className="text-sm text-slate-600">
                  Create projects, assign operations, track materials and expenses for manufacturing jobs.
                </p>
              </CardContent>
            </Card>
            <Card className="rounded-sm border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-sm flex items-center justify-center">
                    <Boxes className="h-5 w-5 text-green-600" />
                  </div>
                  <h3 className="font-semibold">Inventory Control</h3>
                </div>
                <p className="text-sm text-slate-600">
                  Track stock levels, set thresholds, and get alerts when items run low.
                </p>
              </CardContent>
            </Card>
            <Card className="rounded-sm border-slate-200">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-violet-100 rounded-sm flex items-center justify-center">
                    <Bot className="h-5 w-5 text-violet-600" />
                  </div>
                  <h3 className="font-semibold">AI Assistant</h3>
                </div>
                <p className="text-sm text-slate-600">
                  AI monitors your inventory and suggests reorders automatically.
                </p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Entities Tab */}
        <TabsContent value="entities" className="space-y-4">
          {entities.map((entity) => (
            <Card key={entity.name} className="rounded-sm border-slate-200">
              <CardHeader className="pb-2">
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${entity.color.split(' ')[0]}`}>
                    <entity.icon className={`h-5 w-5 ${entity.color.split(' ')[1]}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{entity.name}</CardTitle>
                    <p className="text-sm text-slate-500">{entity.description}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Contains:</h4>
                    <ul className="space-y-1">
                      {entity.contains.map((item, i) => (
                        <li key={`contains-${i}`} className="text-sm text-slate-600 flex items-start gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="bg-slate-50 p-4 rounded-sm">
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Example:</h4>
                    <p className="font-medium text-slate-900 mb-2">{entity.example.title}</p>
                    <ul className="space-y-1">
                      {entity.example.items.map((item, i) => (
                        <li key={`example-${i}`} className="text-sm text-slate-600">• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        {/* Workflow Tab */}
        <TabsContent value="workflow">
          <Card className="rounded-sm border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Complete Workflow Example</CardTitle>
              <p className="text-sm text-slate-500">From client request to project completion</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {workflowSteps.map((step, index) => (
                  <div key={step.step} className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center text-2xl">
                        {step.icon}
                      </div>
                      {index < workflowSteps.length - 1 && (
                        <div className="w-0.5 h-8 bg-slate-200 my-2" />
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className="rounded-sm bg-slate-100 text-slate-700">
                          Step {step.step}
                        </Badge>
                        <h4 className="font-medium text-slate-900">{step.title}</h4>
                      </div>
                      <p className="text-sm text-slate-600">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Relationships Tab */}
        <TabsContent value="relationships">
          <Card className="rounded-sm border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Data Relationships</CardTitle>
              <p className="text-sm text-slate-500">How entities connect to each other</p>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {relationships.map((rel, index) => (
                  <div
                    key={`rel-${rel.from}-${rel.to}`}
                    className="flex items-center gap-4 p-3 bg-slate-50 rounded-sm"
                  >
                    <Badge className="rounded-sm bg-blue-100 text-blue-700 min-w-[120px] justify-center">
                      {rel.from}
                    </Badge>
                    <ArrowRight className="h-4 w-4 text-slate-400" />
                    <Badge className="rounded-sm bg-green-100 text-green-700 min-w-[120px] justify-center">
                      {rel.to}
                    </Badge>
                    <span className="text-sm text-slate-600 flex-1">{rel.desc}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-sm border-slate-200 mt-6">
            <CardHeader>
              <CardTitle className="text-lg">Quick Reference</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 font-medium text-slate-700">Entity</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-700">Purpose</th>
                      <th className="text-left py-3 px-4 font-medium text-slate-700">Key Fields</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 font-medium">Projects</td>
                      <td className="py-3 px-4 text-slate-600">Track manufacturing jobs</td>
                      <td className="py-3 px-4 text-slate-500">name, client, status</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 font-medium">Operations</td>
                      <td className="py-3 px-4 text-slate-600">Track work tasks</td>
                      <td className="py-3 px-4 text-slate-500">name, status, assigned_to</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 font-medium">Standard Items</td>
                      <td className="py-3 px-4 text-slate-600">Reusable templates</td>
                      <td className="py-3 px-4 text-slate-500">materials[], operations[]</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 font-medium">Suppliers</td>
                      <td className="py-3 px-4 text-slate-600">Material vendors</td>
                      <td className="py-3 px-4 text-slate-500">name, email, category</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 font-medium">Inventory</td>
                      <td className="py-3 px-4 text-slate-600">Stock management</td>
                      <td className="py-3 px-4 text-slate-500">current_stock, min_threshold</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 font-medium">Purchase Orders</td>
                      <td className="py-3 px-4 text-slate-600">Supplier orders</td>
                      <td className="py-3 px-4 text-slate-500">items[], total, status</td>
                    </tr>
                    <tr className="border-b border-slate-100">
                      <td className="py-3 px-4 font-medium">Expenses</td>
                      <td className="py-3 px-4 text-slate-600">Cost tracking</td>
                      <td className="py-3 px-4 text-slate-500">amount, category, project_id</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4 font-medium">AI Controller</td>
                      <td className="py-3 px-4 text-slate-600">Smart suggestions</td>
                      <td className="py-3 px-4 text-slate-500">analyzes inventory</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Help;
